// SPDX-FileCopyrightText: Copyright 2024 Dash0 Inc.

"use client";

import React from "react";

import SearchedTextHighlighterWithoutAutoExpansion from "@/components/shared/search/search-text-highlighter-without-auto-expansion";
import { Tag } from "@/components/ui/tag";
import { ImagePreview } from "@/hub/components/image-preview";
import { ViewMetadata } from "@/hub/types";

import { localeCompareIgnoreCase } from "@/lib/utils/string";

type ViewCardProps = {
	view: ViewMetadata;
};

export function ViewCard({ view }: ViewCardProps) {
	const previewImage = view.previews.find((p) => p.type === "image");
	const previewImageSrc = previewImage
		? `/hub/view/preview-image?viewId=${encodeURIComponent(view.id)}&previewName=${encodeURIComponent(previewImage.src)}`
		: undefined;

	return (
		<div
			className="flex flex-col rounded-md p-4 bg-[#1E1E1E] transition-colors gap-3"
			data-testid="hub.view"
			id={`view-${view.id}`}
		>
			<div className="flex flex-col gap-0.5">
				<h3 className="text-lg font-semibold leading-normal mt-0 mb-2 transition-colors text-[#F3F3F3]">
					<SearchedTextHighlighterWithoutAutoExpansion text={view.label} />
				</h3>
				<p className="text-base transition-colors">
					<SearchedTextHighlighterWithoutAutoExpansion text={view.description} />
				</p>
			</div>

			<div className="flex flex-wrap gap-2">
				<Tag className="inline-flex text-grey-550 items-center rounded-tag font-mono text-sm leading-4 px-1 h-4 pointer-events-none">
					[{view.viewType}]
				</Tag>
				{view.tags
					.slice()
					.sort(localeCompareIgnoreCase)
					.map((t) => (
						<Tag
							key={t}
							className="inline-flex text-grey-550 items-center rounded-tag font-mono text-sm leading-4 px-1 h-4 pointer-events-none"
						>
							[<SearchedTextHighlighterWithoutAutoExpansion text={t} />]
						</Tag>
					))}
			</div>

			{previewImage && previewImageSrc && (
				<a href={previewImageSrc} target="_blank" className="cursor-zoom-in">
					<ImagePreview imagePreview={previewImage} src={previewImageSrc} maxWidth={600} maxHeight={200} />
				</a>
			)}
		</div>
	);
}
